import React from 'react';
import { SupplierGapDocumentUploadDetails } from '../../../../store/Models/Api/DataCollectionGapSummaryModels';
import { formatFileSize, getFileIcon } from '../../../../utilities/fileUtils';
import { AIReadOnlyFileListItem } from '../AIReadOnlyFileList';
import { formatTimestampForDisplay } from '../dateTimeUtils';
import DocumentPlusIcon from '../../../shared/DocumentPlusIcon';
import { useLocalizer, useLocalizerFormat } from '../hooks/useLocalizer';
import BackToGapSummaryButton from './BackToGapSummaryButton';
import ProcessNewDocumentsButton from './ProcessNewDocumentsButton';

interface SupplierSubmittedFilesPanelProps {
    sentToEmail: string;
    supplierFiles: SupplierGapDocumentUploadDetails[];
    dateResponseRecieved: string | null;
    onProcessSupplierDocuments?: (
        supplierFiles: SupplierGapDocumentUploadDetails[]
    ) => void;
    onBack: () => void;
    setErrorMessage: (message: string | null) => void;
}

export const SupplierSubmittedFilesPanel: React.FC<SupplierSubmittedFilesPanelProps> = ({
    sentToEmail,
    supplierFiles,
    dateResponseRecieved,
    onProcessSupplierDocuments,
    onBack,
    setErrorMessage
}) => {
    const localize = useLocalizer();
    const localizeFormat = useLocalizerFormat();
    const formattedReceivedAt = formatTimestampForDisplay(dateResponseRecieved);
    const files = supplierFiles.map(file => ({
        id: file.fileId,
        fileName: file.fileName,
        fileSize: file.fileSize
    }));

    const renderDownloadAction = (file: AIReadOnlyFileListItem) => (
        <a
            className="btn btn-link btn-sm"
            href={`/Api/GeneralFile?fileId=${encodeURIComponent(file.id)}`}
            aria-label={`${localize('Download')} ${file.fileName}`}
        >
            <span className="glyphicon glyphicon-download-alt" aria-hidden="true"></span>{' '}
            {localize('Download')}
        </a>
    );

    return (
        <>
            <div className="gap-summary-request-more-files-title gap-summary-sent-page-title">
                <span className="gap-summary-request-more-files-icon">
                    <DocumentPlusIcon className="gap-summary-document-plus-icon" />
                </span>
                <h4>{localize('Fill gaps with supplier input')}</h4>
            </div>

            <section
                className="gap-summary-callout gap-summary-report-ready gap-summary-supplier-report-sent"
                role="status"
            >
                <span className="gap-summary-callout-icon gap-summary-report-ready-icon gap-summary-supplier-report-sent-icon">
                    <span className="glyphicon glyphicon-ok-circle" aria-hidden="true"></span>
                </span>
                <div>
                    <h4>
                        {localize('Gap report sent to')} <strong>{sentToEmail}</strong>
                    </h4>
                    <p>
                        {localize('The email includes the CSV gap report and a secure link for uploading additional documents. Files submitted through the link will appear here automatically. If the supplier sends documents by email or another method, you can upload them when you process new documents.')}
                    </p>
                </div>
            </section>

            <section className="gap-summary-supplier-files-card">
                <div className="gap-summary-supplier-files-header">
                    <div>
                        <h4>{localize('Supplier Uploaded Files')}</h4>
                        {formattedReceivedAt && (
                            <span className="gap-summary-supplier-files-received">
                                {localize('Received')}: {formattedReceivedAt}
                            </span>
                        )}
                    </div>
                    <span className="gap-summary-supplier-files-count">
                        {localizeFormat(files.length === 1 ? '{0} file' : '{0} files', files.length)}
                    </span>
                </div>

                {files.length === 0
                    ? (
                        <p className="gap-summary-empty-state">
                            {localize('No files submitted yet. Files uploaded through the secure link will appear here automatically.')}
                        </p>
                    )
                    : (
                        <div className="gap-summary-supplier-files-table-wrapper">
                            <table
                                className="table toxnot-table-v2 gap-summary-supplier-files-table"
                                aria-label={localize('Supplier Uploaded Files')}
                            >
                                <thead className="sr-only">
                                    <tr>
                                        <th scope="col">{localize('File Name')}</th>
                                        <th scope="col" className="gap-summary-supplier-files-action">
                                            {localize('Download')}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {files.map(file => (
                                        <tr key={file.id}>
                                            <td>
                                                <span className="gap-summary-supplier-file-name">
                                                    <i
                                                        className={`glyphicon ${getFileIcon(file.fileName)}`}
                                                        aria-hidden="true"
                                                    ></i>
                                                    <span className="gap-summary-supplier-file-copy">
                                                        <span className="gap-summary-supplier-file-title">
                                                            {file.fileName}
                                                        </span>
                                                        <span>{formatFileSize(file.fileSize)}</span>
                                                    </span>
                                                </span>
                                            </td>
                                            <td className="gap-summary-supplier-files-action">
                                                {renderDownloadAction(file)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
            </section>

            <BackToGapSummaryButton
                onBack={onBack}
                trailingAction={(
                    <ProcessNewDocumentsButton
                        onProcessNewDocuments={onProcessSupplierDocuments
                            ? () => onProcessSupplierDocuments(supplierFiles)
                            : undefined}
                        setErrorMessage={setErrorMessage}
                    />
                )}
            />
        </>
    );
};

export default SupplierSubmittedFilesPanel;
